import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  RefreshCw, Search, Users, UserX, ArrowUpDown, ArrowUp, ArrowDown,
  X, Send, Megaphone, MessageSquare, Phone, Mail, MapPin, Tag,
  ChevronRight, ChevronDown, ExternalLink, AlertTriangle, CheckCircle, Clock, Zap, Info, UserPlus, Filter
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MERGE_FIELDS = ["{first_name}", "{last_name}", "{agent_name}", "{address}"];
const SMS_MAX = 320;
const URL_PATTERN = /https?:\/\/|www\.|\.com\/|\.net\/|\.org\//i;

// ─── Tag Color System ─────────────────────────────────────────────────────────
function getTagStyle(tag) {
  const u = tag.toUpperCase();
  // RED — alert / do not contact
  if (/UNSUB|DNC|BOUNCED|NOT_INTERESTED|SUNSET|OPT_OUT|STOP|CANCEL|BLOCK/.test(u))
    return "bg-red-50 text-red-700 border border-red-200";
  // AMBER — warning / needs attention
  if (/RETURNED|COLD|UNRESPONSIVE|ATTEMPTED|FOLLOW.?UP|AI_NOT/.test(u))
    return "bg-amber-50 text-amber-700 border border-amber-200";
  // GREEN — active / positive / hot
  if (/^BUYER$|^SELLER$|HOT|VIP|ACTIVE|SIGNED|CLOSED|APPROVED|REFERRED|PRIORITY/.test(u))
    return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  // VIOLET — lead source / platform
  if (/YLOPO|ZILLOW|REALTOR|TRULIA|REDFIN|FACEBOOK|FB|DIRECT.?CONNECT|ORGANIC/.test(u))
    return "bg-violet-50 text-violet-700 border border-violet-200";
  // TEAL — market / email / campaigns
  if (/MARKET|LISTING|ALERT|EMAIL|NEWSLETTER|CAMPAIGN|MLS/.test(u))
    return "bg-orange-50 text-orange-700 border border-orange-200";
  // SLATE — zip codes, locations, numeric
  if (/^\d{5}$/.test(tag) || /SAN.?DIEGO|LAS.?VEGAS|LOS.?ANGELES|PHOENIX|NEVADA|CALIFORNIA/.test(u))
    return "bg-slate-100 text-slate-600 border border-slate-200";
  // BLUE — default branded
  return "bg-[#FFF0EB] text-[#0F1419] border border-[#D1D5DB]";
}

function TagPill({ tag }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getTagStyle(tag)}`}>
      {tag}
    </span>
  );
}

// ─── Stage Badge ──────────────────────────────────────────────────────────────
function StageBadge({ stage }) {
  if (!stage) return null;
  const u = stage.toUpperCase();
  let cls = "bg-slate-100 text-slate-600 border-slate-200";
  if (/HOT|ACTIVE|SIGNED/.test(u)) cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (/COLD|CLOSED/.test(u)) cls = "bg-slate-100 text-slate-500 border-slate-200";
  else if (/ATTEMPTED|CONTACT/.test(u)) cls = "bg-amber-50 text-amber-700 border-amber-200";
  else if (/NURTURE|LEAD/.test(u)) cls = "bg-[#FFF0EB] text-[#0F1419] border-[#D1D5DB]";
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${cls}`}>{stage}</span>;
}

// ─── Multi-Select Filter Dropdown ────────────────────────────────────────────
function MultiSelectDropdown({ label, options, selected, onToggle, onClear, renderOption, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = search
    ? options.filter(o => (typeof o === "string" ? o : o.label).toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className={`input w-auto text-sm flex items-center gap-1.5 pr-2 whitespace-nowrap ${
          selected.length > 0 ? "border-[#FF5C35] bg-[#FFF0EB]" : ""
        }`}
      >
        <Filter size={12} className={selected.length > 0 ? "text-[#FF5C35]" : "text-slate-400"} />
        {label}
        {selected.length > 0 && (
          <span className="ml-1 bg-[#FF5C35] text-white text-[10px] font-bold rounded-full w-4.5 h-4.5 flex items-center justify-center px-1.5 py-0.5 leading-none">
            {selected.length}
          </span>
        )}
        <ChevronDown size={12} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden" style={{ maxHeight: "320px" }}>
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-[#FF5C35]"
                placeholder={`Search ${label.toLowerCase()}...`}
                autoFocus
              />
            </div>
          </div>
          {/* Selected pills */}
          {selected.length > 0 && (
            <div className="px-2 pt-2 pb-1 flex flex-wrap gap-1 border-b border-slate-100">
              {selected.map(s => (
                <span key={s} className="inline-flex items-center gap-1 bg-[#FF5C35] text-white text-[10px] font-medium rounded-full px-2 py-0.5">
                  {s}
                  <button onClick={() => onToggle(s)} className="hover:bg-white/20 rounded-full"><X size={10} /></button>
                </span>
              ))}
              <button onClick={onClear} className="text-[10px] text-slate-400 hover:text-red-500 ml-1">Clear all</button>
            </div>
          )}
          {/* Options */}
          <div className="overflow-y-auto" style={{ maxHeight: "220px" }}>
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-400 p-3 text-center">No matches</p>
            ) : filtered.map(o => {
              const val = typeof o === "string" ? o : o.value;
              const lbl = typeof o === "string" ? o : o.label;
              const count = typeof o === "object" ? o.count : null;
              const isSelected = selected.includes(val);
              return (
                <button
                  key={val}
                  onClick={() => onToggle(val)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                    isSelected ? "bg-[#FFF0EB] text-[#FF5C35] font-medium" : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                    isSelected ? "bg-[#FF5C35] border-[#FF5C35]" : "border-slate-300"
                  }`}>
                    {isSelected && <CheckCircle size={10} className="text-white" />}
                  </span>
                  <span className="truncate flex-1 text-left">{renderOption ? renderOption(o) : lbl}</span>
                  {count !== null && count !== undefined && (
                    <span className="text-[10px] text-slate-400 shrink-0">({count})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Contact Detail Drawer ────────────────────────────────────────────────────
function ContactDrawer({ contactId, onClose, onDelete, campaigns, onSendSMS, onEnroll, onToggleOptOut }) {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [enrollMsg, setEnrollMsg] = useState("");

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    axios.get(`${API}/api/contacts/${contactId}`)
      .then(r => setContact(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [contactId]);

  const sendReply = async () => {
    if (!replyText.trim() || !contact) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/messages/send`, { contact_id: contactId, body: replyText });
      setContact(prev => ({ ...prev, messages: [...(prev.messages || []), r.data] }));
      setReplyText("");
    } catch (e) { alert(e.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  const enroll = async () => {
    if (!selectedCampaign) return;
    setEnrolling(true);
    try {
      await axios.post(`${API}/api/campaigns/enroll`, { campaign_id: selectedCampaign, contact_ids: [contactId] });
      setEnrollMsg("Enrolled!");
      setTimeout(() => setEnrollMsg(""), 3000);
      const r = await axios.get(`${API}/api/contacts/${contactId}`);
      setContact(r.data);
    } catch (e) { setEnrollMsg(e.response?.data?.detail || "Failed"); }
    setEnrolling(false);
  };

  const toggleOptOut = async () => {
    if (!contact) return;
    await axios.patch(`${API}/api/contacts/${contactId}/optout`, { opted_out: !contact.opted_out });
    setContact(prev => ({ ...prev, opted_out: !prev.opted_out }));
    onToggleOptOut && onToggleOptOut();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="contact-drawer">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-7 h-7 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !contact ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">Contact not found</div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-200 bg-white shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FFF0EB] text-[#0F1419] flex items-center justify-center text-base font-bold shrink-0">
                    {(contact.first_name?.[0] || "") + (contact.last_name?.[0] || "")}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold font-heading text-slate-900">{contact.first_name} {contact.last_name}</h2>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StageBadge stage={contact.stage} />
                      {contact.opted_out && <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5"><UserX size={10} />Opted Out</span>}
                    </div>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-drawer"><X size={20} /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Contact Info */}
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Contact Info</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Phone size={13} className="text-slate-400 shrink-0" />
                    <span>{contact.phone || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700 min-w-0">
                    <Mail size={13} className="text-slate-400 shrink-0" />
                    <span className="truncate">{contact.email || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <ExternalLink size={13} className="text-slate-400 shrink-0" />
                    <span>{contact.source || "—"}</span>
                  </div>
                  {contact.fub_data?.price && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <span className="text-slate-400 text-xs">Price</span>
                      <span className="font-medium">${Number(contact.fub_data.price).toLocaleString()}</span>
                    </div>
                  )}
                  {contact.fub_data?.assigned_to && (
                    <div className="flex items-center gap-2 text-slate-700 col-span-2">
                      <span className="text-slate-400 text-xs">Assigned to</span>
                      <span>{contact.fub_data.assigned_to}</span>
                    </div>
                  )}
                  {contact.fub_data?.last_activity && (
                    <div className="flex items-center gap-2 text-slate-700 col-span-2">
                      <Clock size={13} className="text-slate-400 shrink-0" />
                      <span className="text-xs">Last FUB activity: {new Date(contact.fub_data.last_activity).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {(contact.tags || []).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-400 mb-1.5">Tags</p>
                    <div className="flex flex-wrap gap-1.5">
                      {contact.tags.map(t => <TagPill key={t} tag={t} />)}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Actions</h3>
                <div className="flex flex-wrap gap-2">
                  <a href={`/blast`} className="btn-primary py-1.5 px-3 text-xs no-underline">
                    <Send size={12} /> Send Blast
                  </a>
                  <button onClick={toggleOptOut} className={`${contact.opted_out ? "btn-primary" : "btn-secondary"} py-1.5 px-3 text-xs`} data-testid="drawer-optout-btn">
                    <UserX size={12} /> {contact.opted_out ? "Re-activate" : "Mark Opted Out"}
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ${contact.first_name} ${contact.last_name}? This removes the contact and all their message history.`)) return;
                      try {
                        await axios.delete(`${API}/api/contacts/${contactId}`);
                        if (onDelete) onDelete(contactId);
                        onClose();
                      } catch (e) { alert(e.response?.data?.detail || "Delete failed"); }
                    }}
                    className="btn-secondary py-1.5 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X size={12} /> Delete
                  </button>
                </div>

                {/* Campaign Enrollment */}
                <div className="mt-3 flex items-center gap-2">
                  <select value={selectedCampaign} onChange={e => setSelectedCampaign(e.target.value)} className="input text-xs py-1.5 flex-1" data-testid="drawer-campaign-select">
                    <option value="">Enroll in campaign...</option>
                    {(campaigns || []).filter(c => c.status === "active").map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button onClick={enroll} disabled={!selectedCampaign || enrolling} className="btn-primary py-1.5 px-3 text-xs shrink-0" data-testid="drawer-enroll-btn">
                    <Megaphone size={12} /> {enrollMsg || (enrolling ? "..." : "Enroll")}
                  </button>
                </div>

                {/* Active campaigns */}
                {(contact.campaigns || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {contact.campaigns.map(c => (
                      <span key={c.id} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5">
                        <Megaphone size={9} /> {c.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Message History */}
              <div className="px-6 py-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
                  SMS History ({(contact.messages || []).length})
                </h3>
                {(contact.messages || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No messages yet</p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {contact.messages.map(m => (
                      <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-xs px-3 py-2 rounded-xl text-xs leading-relaxed ${m.direction === "outbound" ? "bg-[#FF5C35] text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                          <p>{m.body}</p>
                          <div className={`text-xs mt-0.5 ${m.direction === "outbound" ? "text-[#D1D5DB]" : "text-slate-400"}`}>
                            {new Date(m.created_at).toLocaleDateString()} {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {m.direction === "outbound" && <span className={`ml-1 ${m.status === "delivered" ? "text-green-300" : "text-red-300"}`}>{m.status}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quick Reply */}
                {!contact.opted_out && (
                  <div className="mt-3 flex gap-2">
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }}}
                      rows={2}
                      placeholder="Quick reply... (Enter to send)"
                      className="input text-xs resize-none flex-1"
                      data-testid="drawer-reply-input"
                    />
                    <button onClick={sendReply} disabled={sending || !replyText.trim()} className="btn-primary px-3 self-end" data-testid="drawer-send-btn">
                      <Send size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Filter Section (searchable multi-column checkbox grid) ──────────────────
function FilterSection({ label, items, selected, toggle, clear, testPrefix, hasLogic, tagLogic, setTagLogic }) {
  const [q, setQ] = useState("");
  const filtered = items.filter(i => !q || i.label.toLowerCase().includes(q.toLowerCase()));
  const mid = Math.ceil(filtered.length / 2);
  const col1 = filtered.slice(0, mid);
  const col2 = filtered.slice(mid);

  return (
    <section data-testid={`${testPrefix}-selector`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
          {label}
          {selected.length > 0 && (
            <span className="bg-[#FF5C35] text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{selected.length}</span>
          )}
        </h3>
        <div className="flex items-center gap-1">
          {hasLogic && (
            <>
              <span className="text-xs text-slate-400 mr-1">Logic:</span>
              <button onClick={() => setTagLogic("any")}
                className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${tagLogic === "any" ? "bg-[#FF5C35] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid="tag-logic-any">ANY</button>
              <button onClick={() => setTagLogic("all")}
                className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${tagLogic === "all" ? "bg-[#FF5C35] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid="tag-logic-all">ALL</button>
            </>
          )}
          {selected.length > 0 && (
            <button onClick={clear} className="text-xs text-slate-400 hover:text-red-500 ml-1">Clear all</button>
          )}
        </div>
      </div>
      <div className="relative mb-2">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}...`}
          className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-[#FF5C35] bg-slate-50" />
      </div>
      <div className="border border-slate-200 rounded-lg bg-white max-h-44 overflow-y-auto">
        <div className="grid grid-cols-2 divide-x divide-slate-100">
          {[col1, col2].map((col, ci) => (
            <div key={ci} className="divide-y divide-slate-50">
              {col.map(item => (
                <label key={item.value}
                  className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 transition-colors text-xs ${
                    selected.includes(item.value) ? "bg-[#FFF0EB]" : ""
                  }`}
                  data-testid={`${testPrefix}-${item.value}`}>
                  <input type="checkbox" checked={selected.includes(item.value)}
                    onChange={() => toggle(item.value)}
                    className="w-3.5 h-3.5 rounded border-slate-300 text-[#FF5C35] focus:ring-[#FF5C35] shrink-0 accent-[#FF5C35]" />
                  <span className="truncate font-medium text-slate-700">{item.label}</span>
                  {item.count > 0 && <span className="text-slate-400 ml-auto shrink-0">({item.count.toLocaleString()})</span>}
                </label>
              ))}
              {col.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">No matches</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Quick Blast Panel ────────────────────────────────────────────────────────
function QuickBlastPanel({ stats, onClose }) {
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedPonds, setSelectedPonds] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [tagLogic, setTagLogic] = useState("any");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState({ contacts: [], total: 0, breakdown: [] });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Load ponds from FUB
  useEffect(() => {
    axios.get(`${API}/api/fub/ponds`).then(r => setPonds(r.data.ponds || [])).catch(() => {});
  }, []);

  const hasFilters = selectedStages.length > 0 || selectedTags.length > 0 || selectedSources.length > 0 || selectedAgents.length > 0 || selectedPonds.length > 0 || search;

  useEffect(() => {
    if (!hasFilters) { setPreview({ contacts: [], total: 0, breakdown: [] }); return; }
    setLoadingPreview(true);
    const t = setTimeout(() => {
      axios.get(`${API}/api/contacts/filter`, {
        params: {
          stages: selectedStages.join(","),
          tags: selectedTags.join(","),
          tag_logic: tagLogic,
          sources: selectedSources.join(","),
          agents: selectedAgents.join(","),
          pond_id: selectedPonds.join(","),
          search,
        }
      })
        .then(r => setPreview(r.data))
        .catch(() => {})
        .finally(() => setLoadingPreview(false));
    }, 350);
    return () => clearTimeout(t);
  }, [selectedStages, selectedTags, tagLogic, selectedSources, selectedAgents, selectedPonds, search, hasFilters]);

  const toggleStage = s => setSelectedStages(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const toggleTag = t => setSelectedTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);
  const toggleSource = s => setSelectedSources(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const toggleAgent = a => setSelectedAgents(p => p.includes(a) ? p.filter(x => x !== a) : [...p, a]);
  const togglePond = p => setSelectedPonds(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const sendBlast = async () => {
    if (!body.trim() || preview.total === 0) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/blasts`, {
        contact_ids: preview.contacts.map(c => c.id),
        body,
      });
      setResult(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Blast failed"); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="quick-blast-panel">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold font-heading text-slate-900 flex items-center gap-2">
                <Zap size={16} className="text-[#FF5C35]" /> Quick Blast
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Filter by stage & tag, then send a targeted SMS blast</p>
            </div>
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="close-blast-panel">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Result */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="quick-blast-result">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-green-800">
                  {result.mock ? "Simulated" : "Blast sent!"} — {result.sent} message{result.sent !== 1 ? "s" : ""}
                </p>
                <button onClick={() => setResult(null)} className="text-green-600 hover:text-green-800"><X size={14} /></button>
              </div>
              <p className="text-xs text-green-600 mt-1">Failed: {result.failed} · Opted out skipped: {result.opted_out}</p>
              {result.mock && <p className="text-xs text-green-500 mt-1 flex items-center gap-1"><Info size={11} />Add SignalWire credentials in Settings to send real SMS</p>}
            </div>
          )}

          {/* ── Searchable Multi-Column Selectors ── */}
          <FilterSection label="Stages" testPrefix="blast-stage"
            items={(stats.stages || []).map(s => ({ value: s.stage, label: s.stage, count: s.count }))}
            selected={selectedStages} toggle={toggleStage} clear={() => setSelectedStages([])} />
          <FilterSection label="Tags" testPrefix="blast-tag" hasLogic tagLogic={tagLogic} setTagLogic={setTagLogic}
            items={(stats.top_tags || []).map(t => ({ value: t.tag, label: t.tag, count: t.count }))}
            selected={selectedTags} toggle={toggleTag} clear={() => setSelectedTags([])} />
          <FilterSection label="Sources" testPrefix="blast-source"
            items={(stats.sources || []).map(s => ({ value: s.source, label: s.source, count: s.count }))}
            selected={selectedSources} toggle={toggleSource} clear={() => setSelectedSources([])} />
          <FilterSection label="Agents" testPrefix="blast-agent"
            items={(stats.agents || []).map(a => ({ value: String(a.id), label: a.name, count: a.count }))}
            selected={selectedAgents} toggle={toggleAgent} clear={() => setSelectedAgents([])} />
          <FilterSection label="Ponds" testPrefix="blast-pond"
            items={ponds.map(p => ({ value: String(p.id), label: p.name, count: 0 }))}
            selected={selectedPonds} toggle={togglePond} clear={() => setSelectedPonds([])} />

          {/* Name search */}
          <section>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Narrow Down</h3>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name..." className="input pl-8 text-sm w-full"
                data-testid="blast-filter-search" />
            </div>
          </section>

          {/* Live Preview */}
          <section
            className={`rounded-xl border-2 p-4 transition-colors ${
              preview.total > 0 ? "border-[#D1D5DB] bg-[#FFF0EB]/60" : "border-slate-200 bg-slate-50"
            }`}
            data-testid="blast-preview"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Recipients Preview</h3>
              {loadingPreview && <div className="w-3.5 h-3.5 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" />}
            </div>

            {!hasFilters ? (
              <p className="text-xs text-slate-400">Select at least one stage, tag, or source to preview contacts</p>
            ) : loadingPreview && preview.total === 0 ? (
              <p className="text-xs text-slate-400">Loading...</p>
            ) : preview.total === 0 ? (
              <p className="text-xs text-slate-500">No active contacts match these filters</p>
            ) : (
              <>
                <p className="text-3xl font-bold text-[#FF5C35] leading-none mb-1" data-testid="blast-preview-count">
                  {preview.total}
                  <span className="text-sm font-normal text-slate-500 ml-2">contacts selected</span>
                </p>
                {/* Breakdown by stage */}
                {preview.breakdown.length > 1 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
                    {preview.breakdown.map(b => (
                      <span key={b.stage} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white border border-[#D1D5DB] text-slate-600">
                        <span className="font-medium text-[#0F1419]">{b.count}</span> {b.stage}
                      </span>
                    ))}
                  </div>
                )}
                {/* Sample contacts */}
                <div className="space-y-1.5">
                  {preview.contacts.slice(0, 4).map(c => (
                    <div key={c.id} className="flex items-center gap-2 text-xs text-slate-600">
                      <div className="w-6 h-6 rounded-full bg-[#FFF0EB] text-[#0F1419] flex items-center justify-center font-bold shrink-0" style={{ fontSize: "9px" }}>
                        {(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}
                      </div>
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      {c.stage && <StageBadge stage={c.stage} />}
                    </div>
                  ))}
                  {preview.total > 4 && (
                    <p className="text-xs text-slate-400 pl-8">+{preview.total - 4} more contacts</p>
                  )}
                </div>
              </>
            )}
          </section>

          {/* Message Composer */}
          <section>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2.5">Message</h3>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {MERGE_FIELDS.map(f => (
                <button
                  key={f}
                  onClick={() => setBody(b => b + f)}
                  className="text-xs px-2.5 py-1 bg-[#FFF0EB] text-[#0F1419] border border-[#D1D5DB] rounded-full hover:bg-[#FFF0EB] transition-colors"
                  data-testid={`blast-merge-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={4}
              placeholder={`Hi {first_name}, this is your agent from ABC Realty...`}
              className="input resize-none text-sm w-full"
              data-testid="quick-blast-message"
            />
            <div className="space-y-1 mt-1">
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${body.length > SMS_MAX ? "text-red-600" : body.length > 250 ? "text-amber-600" : "text-slate-400"}`}>
                  {body.length}/{SMS_MAX} chars · {body.length <= 160 ? 1 : Math.ceil(body.length / 153)} segment{body.length > 160 ? "s" : ""}
                </span>
                <span className="text-xs text-slate-400 flex items-center gap-1"><Info size={10} />Opt-out appended</span>
              </div>
              <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${body.length > SMS_MAX ? "bg-red-500" : body.length > 250 ? "bg-amber-500" : "bg-[#FF5C35]"}`} style={{ width: `${Math.min((body.length / SMS_MAX) * 100, 100)}%` }} />
              </div>
              {body.length > SMS_MAX && <p className="text-xs text-red-600">Message too long — carriers will block delivery.</p>}
              {URL_PATTERN.test(body) && <p className="text-xs text-red-600">URLs not allowed in blasts — carriers flag as spam.</p>}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-200 bg-white shrink-0">
          <button
            onClick={sendBlast}
            disabled={sending || preview.total === 0 || !body.trim() || body.length > SMS_MAX || URL_PATTERN.test(body)}
            className="btn-primary w-full justify-center py-3 text-sm"
            data-testid="quick-blast-send-btn"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={15} />
                Blast to {preview.total} Contact{preview.total !== 1 ? "s" : ""}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sort Button ──────────────────────────────────────────────────────────────
function SortBtn({ field, label, current, order, onChange }) {
  const active = current === field;
  return (
    <button
      onClick={() => onChange(field, active && order === "asc" ? "desc" : "asc")}
      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border font-medium transition-colors ${active ? "bg-[#FF5C35] text-white border-[#FF5C35]" : "bg-white text-slate-600 border-slate-300 hover:border-[#FF5C35] hover:text-[#FF5C35]"}`}
      data-testid={`sort-${field}`}
    >
      {label}
      {active ? (order === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-50" />}
    </button>
  );
}

// ─── Add Contact Modal ───────────────────────────────────────────────────────
function AddContactModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", stage: "Lead" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!form.first_name.trim()) { setError("First name is required"); return; }
    if (!form.phone.trim()) { setError("Phone number is required"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/api/contacts`, form);
      onCreated();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create contact");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="add-contact-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold font-heading text-slate-900">Add Contact</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">First Name *</label>
              <input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} className="input text-sm" placeholder="John" data-testid="add-first-name" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Last Name</label>
              <input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} className="input text-sm" placeholder="Doe" data-testid="add-last-name" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Phone Number *</label>
            <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="input text-sm" placeholder="(702) 555-1234" data-testid="add-phone" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
            <input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="input text-sm" placeholder="john@example.com" data-testid="add-email" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Stage</label>
            <select value={form.stage} onChange={e => setForm(f => ({...f, stage: e.target.value}))} className="input text-sm" data-testid="add-stage">
              <option value="Lead">Lead</option>
              <option value="Hot Lead">Hot Lead</option>
              <option value="Attempted Contact">Attempted Contact</option>
              <option value="Spoke with customer">Spoke with customer</option>
              <option value="Active Client">Active Client</option>
              <option value="Nurture">Nurture</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-1" data-testid="save-contact-btn">
            {saving ? "Saving..." : "Add Contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStages, setFilterStages] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [filterSources, setFilterSources] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterPonds, setFilterPonds] = useState([]);
  const [tagLogic, setTagLogic] = useState("any");
  const [filterOptOut, setFilterOptOut] = useState("");
  // Live FUB filter options
  const [fubTags, setFubTags] = useState([]);
  const [fubAgents, setFubAgents] = useState([]);
  const [fubPonds, setFubPonds] = useState([]);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [syncMsg, setSyncMsg] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showBlastPanel, setShowBlastPanel] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);

  const searchRef = useRef(search);
  searchRef.current = search;

  const load = useCallback(async (searchOverride) => {
    setLoading(true);
    try {
      const s = searchOverride !== undefined ? searchOverride : searchRef.current;
      const params = { page, limit: 25, sort_by: sortBy, sort_order: sortOrder };
      if (s) params.search = s;
      if (filterStages.length) params.stages = filterStages.join(",");
      if (filterTags.length) { params.tags = filterTags.join(","); params.tag_logic = tagLogic; }
      if (filterSources.length) params.sources = filterSources.join(",");
      if (filterAgents.length) params.agent_id = filterAgents.join(",");
      if (filterPonds.length) params.pond_id = filterPonds.join(",");
      if (filterOptOut !== "") params.opted_out = filterOptOut === "true";
      const cr = await axios.get(`${API}/api/contacts`, { params });
      setContacts(cr.data.contacts);
      setTotal(cr.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, filterStages, filterTags, tagLogic, filterSources, filterAgents, filterPonds, filterOptOut, sortBy, sortOrder]);

  // Load stats only once on mount and when filters change (not on search)
  useEffect(() => {
    axios.get(`${API}/api/contacts/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  // Fetch live FUB filter options (tags, stages, agents, ponds)
  useEffect(() => {
    axios.get(`${API}/api/fub/tags`).then(r => setFubTags(r.data.tags || [])).catch(() => {});
    axios.get(`${API}/api/onboarding/team-agents`).then(r => setFubAgents(r.data.agents || [])).catch(() => {});
    axios.get(`${API}/api/fub/ponds`).then(r => setFubPonds(r.data.ponds || [])).catch(() => {});
  }, []);

  // Load contacts when non-search filters change
  useEffect(() => { load(); }, [load]);

  // Debounced search — 300ms
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(search); }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/api/campaigns`).then(r => setCampaigns(r.data)).catch(() => {});
  }, []);

  const handleSort = (field, order) => { setSortBy(field); setSortOrder(order); setPage(1); };

  const syncFUB = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      await axios.post(`${API}/api/fub/sync`);
      setSyncMsg("Syncing contacts in background...");
      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/api/fub/sync-status`);
          const { status, synced, total, error } = r.data;
          if (status === "running") {
            setSyncMsg(`Syncing... ${synced.toLocaleString()} contacts imported so far`);
          } else if (status === "done") {
            setSyncMsg(`Successfully synced ${synced.toLocaleString()} contacts (${total.toLocaleString()} total)`);
            setSyncing(false);
            clearInterval(poll);
            load();
          } else if (status === "error") {
            setSyncMsg(`Sync error: ${error || "Unknown error"}`);
            setSyncing(false);
            clearInterval(poll);
          }
        } catch { /* ignore poll errors */ }
      }, 3000);
    } catch (e) {
      setSyncMsg(e.response?.data?.detail || "Sync failed. Check your FUB API key in Settings.");
      setSyncing(false);
    }
  };

  const toggleOptOut = async (e, contact) => {
    e.stopPropagation();
    await axios.patch(`${API}/api/contacts/${contact.id}/optout`, { opted_out: !contact.opted_out });
    load();
  };

  return (
    <div className="p-4 md:p-8" data-testid="contacts-page">
      {selectedContactId && (
        <ContactDrawer
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
          onDelete={() => { setSelectedContactId(null); load(); }}
          campaigns={campaigns}
          onToggleOptOut={load}
        />
      )}

      {showBlastPanel && (
        <QuickBlastPanel stats={stats} onClose={() => setShowBlastPanel(false)} />
      )}

      {showAddContact && (
        <AddContactModal onClose={() => setShowAddContact(false)} onCreated={load} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">Contacts</h1>
          <p className="text-slate-500 text-sm mt-0.5">{total.toLocaleString()} contacts</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowAddContact(true)}
            className="btn-secondary text-sm"
            data-testid="add-contact-btn"
          >
            <UserPlus size={15} /> <span className="hidden sm:inline">Add</span> Contact
          </button>
          <button
            onClick={() => setShowBlastPanel(true)}
            className="btn-primary bg-[#FF5C35] hover:bg-[#E84E29] text-sm"
            data-testid="quick-blast-btn"
          >
            <Zap size={15} /> Quick Blast
          </button>
          <button onClick={syncFUB} disabled={syncing} className="btn-secondary text-sm" data-testid="sync-fub-btn">
            <RefreshCw size={15} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing..." : "Sync FUB"}
          </button>
        </div>
      </div>

      {syncMsg && (
        <div className={`mb-4 px-4 py-3 rounded-md text-sm border ${syncMsg.includes("Successfully") ? "bg-green-50 text-green-700 border-green-200" : syncMsg.includes("error") ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="sync-message">
          {syncing && <span className="inline-block w-3 h-3 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mr-2 align-middle" />}
          {syncMsg}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Total", value: stats.total || 0, icon: Users, color: "text-[#FF5C35] bg-[#FFF0EB]" },
          { label: "Opted Out", value: stats.opted_out || 0, icon: UserX, color: "text-red-600 bg-red-50" },
          ...((stats.stages || []).slice(0, 2).map(s => ({ label: s.stage, value: s.count, icon: ChevronRight, color: "text-slate-500 bg-slate-50" })))
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card px-4 py-3 flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
              <Icon size={15} />
            </div>
            <div>
              <p className="text-lg font-semibold font-heading text-slate-900">{value.toLocaleString()}</p>
              <p className="text-xs text-slate-500 truncate">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card mb-4 p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-0 sm:min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} className="input pl-9 text-sm" placeholder="Search name, email, phone..." data-testid="contact-search" />
          </div>

          {/* Multi-select: Stages */}
          <MultiSelectDropdown
            label="Stages"
            testId="filter-stages"
            options={(() => {
              // Merge FUB stages with stats — FUB stages are canonical, stats provide counts
              const stageMap = {};
              (stats.stages || []).forEach(s => { stageMap[s.stage] = s.count; });
              // Use FUB stages as base if available, fall back to stats
              const fubStages = (stats.stages || []).map(s => s.stage);
              const allStages = [...new Set([...fubStages])];
              return allStages.map(s => ({ value: s, label: s, count: stageMap[s] || 0 }));
            })()}
            selected={filterStages}
            onToggle={s => { setFilterStages(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]); setPage(1); }}
            onClear={() => { setFilterStages([]); setPage(1); }}
          />

          {/* Multi-select: Tags with ANY/ALL logic */}
          <div className="flex items-center gap-0.5">
            <MultiSelectDropdown
              label="Tags"
              testId="filter-tags"
              options={(() => {
                const tagCountMap = {};
                (stats.top_tags || []).forEach(t => { tagCountMap[t.tag] = t.count; });
                // Merge FUB live tags with stats tags — FUB tags are canonical
                const allTags = [...new Set([...fubTags, ...(stats.top_tags || []).map(t => t.tag)])].sort();
                return allTags.map(t => ({ value: t, label: t, count: tagCountMap[t] || 0 }));
              })()}
              selected={filterTags}
              onToggle={t => { setFilterTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]); setPage(1); }}
              onClear={() => { setFilterTags([]); setPage(1); }}
            />
            {filterTags.length > 1 && (
              <div className="flex items-center gap-0.5 ml-0.5">
                <button
                  onClick={() => setTagLogic("any")}
                  className={`px-2 py-1 rounded text-[10px] font-bold transition-colors ${tagLogic === "any" ? "bg-[#FF5C35] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                  data-testid="contacts-tag-logic-any"
                >ANY</button>
                <button
                  onClick={() => setTagLogic("all")}
                  className={`px-2 py-1 rounded text-[10px] font-bold transition-colors ${tagLogic === "all" ? "bg-[#FF5C35] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                  data-testid="contacts-tag-logic-all"
                >ALL</button>
              </div>
            )}
          </div>

          {/* Multi-select: Sources */}
          <MultiSelectDropdown
            label="Sources"
            testId="filter-sources"
            options={(stats.sources || []).map(s => ({ value: s.source, label: s.source, count: s.count }))}
            selected={filterSources}
            onToggle={s => { setFilterSources(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]); setPage(1); }}
            onClear={() => { setFilterSources([]); setPage(1); }}
          />

          {/* Multi-select: Agent */}
          {fubAgents.length > 0 && (
            <MultiSelectDropdown
              label="Agent"
              testId="filter-agents"
              options={fubAgents.map(a => ({ value: String(a.id), label: a.name || a.email || `Agent #${a.id}` }))}
              selected={filterAgents}
              onToggle={a => { setFilterAgents(p => p.includes(a) ? p.filter(x => x !== a) : [...p, a]); setPage(1); }}
              onClear={() => { setFilterAgents([]); setPage(1); }}
            />
          )}

          {/* Multi-select: Pond */}
          {fubPonds.length > 0 && (
            <MultiSelectDropdown
              label="Ponds"
              testId="filter-ponds"
              options={fubPonds.map(p => ({ value: String(p.id), label: p.name }))}
              selected={filterPonds}
              onToggle={p => { setFilterPonds(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]); setPage(1); }}
              onClear={() => { setFilterPonds([]); setPage(1); }}
            />
          )}

          {/* Status */}
          <select value={filterOptOut} onChange={e => { setFilterOptOut(e.target.value); setPage(1); }} className="input w-auto text-sm" data-testid="filter-optout">
            <option value="">All Status</option>
            <option value="false">Active</option>
            <option value="true">Opted Out</option>
          </select>
        </div>

        {/* Active filter pills */}
        {(filterStages.length > 0 || filterTags.length > 0 || filterSources.length > 0 || filterAgents.length > 0 || filterPonds.length > 0) && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Active:</span>
            {filterStages.map(s => (
              <span key={`s-${s}`} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-0.5 text-[11px] font-medium">
                {s} <button onClick={() => { setFilterStages(p => p.filter(x => x !== s)); setPage(1); }}><X size={10} /></button>
              </span>
            ))}
            {filterTags.map(t => (
              <span key={`t-${t}`} className="inline-flex items-center gap-1 bg-[#FFF0EB] text-[#FF5C35] border border-[#FF5C35]/20 rounded-full px-2.5 py-0.5 text-[11px] font-medium">
                {t} {filterTags.length > 1 && <span className="text-[9px] opacity-60">({tagLogic})</span>}
                <button onClick={() => { setFilterTags(p => p.filter(x => x !== t)); setPage(1); }}><X size={10} /></button>
              </span>
            ))}
            {filterSources.map(s => (
              <span key={`src-${s}`} className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2.5 py-0.5 text-[11px] font-medium">
                {s} <button onClick={() => { setFilterSources(p => p.filter(x => x !== s)); setPage(1); }}><X size={10} /></button>
              </span>
            ))}
            {filterAgents.map(a => {
              const agent = fubAgents.find(ag => String(ag.id) === a);
              return (
                <span key={`a-${a}`} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-0.5 text-[11px] font-medium">
                  {agent?.name || `Agent #${a}`} <button onClick={() => { setFilterAgents(p => p.filter(x => x !== a)); setPage(1); }}><X size={10} /></button>
                </span>
              );
            })}
            {filterPonds.map(p => {
              const pond = fubPonds.find(pd => String(pd.id) === p);
              return (
                <span key={`p-${p}`} className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2.5 py-0.5 text-[11px] font-medium">
                  {pond?.name || `Pond #${p}`} <button onClick={() => { setFilterPonds(prev => prev.filter(x => x !== p)); setPage(1); }}><X size={10} /></button>
                </span>
              );
            })}
          </div>
        )}

        {/* Sort Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 font-medium">Sort:</span>
          <SortBtn field="name" label="Name" current={sortBy} order={sortOrder} onChange={handleSort} />
          <SortBtn field="stage" label="Stage" current={sortBy} order={sortOrder} onChange={handleSort} />
          <SortBtn field="source" label="Source" current={sortBy} order={sortOrder} onChange={handleSort} />
          <SortBtn field="created_at" label="Date Added" current={sortBy} order={sortOrder} onChange={handleSort} />
          {(search || filterStages.length || filterTags.length || filterSources.length || filterAgents.length || filterPonds.length || filterOptOut) && (
            <button onClick={() => { setSearch(""); setFilterStages([]); setFilterTags([]); setFilterSources([]); setFilterAgents([]); setFilterPonds([]); setFilterOptOut(""); setPage(1); }} className="ml-auto text-xs text-slate-500 hover:text-red-500 flex items-center gap-1" data-testid="clear-filters">
              <X size={12} /> Clear all filters
            </button>
          )}
        </div>
      </div>

      {/* Table — desktop only */}
      <div className="card overflow-hidden hidden md:block" data-testid="contacts-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              {["Contact", "Phone", "Stage", "Tags", "Source", "Status", ""].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="text-center py-14 text-slate-400 text-sm">
                <div className="flex justify-center"><div className="w-5 h-5 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /></div>
              </td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-14 text-slate-400 text-sm">No contacts found</td></tr>
            ) : contacts.map(c => (
              <tr
                key={c.id}
                onClick={() => setSelectedContactId(c.id)}
                className="hover:bg-[#FFF0EB]/40 transition-colors cursor-pointer group"
                data-testid={`contact-row-${c.id}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${c.opted_out ? "bg-slate-100 text-slate-400" : "bg-[#FFF0EB] text-[#0F1419]"}`}>
                      {(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 group-hover:text-[#0F1419] transition-colors">{c.first_name} {c.last_name}</p>
                      <p className="text-xs text-slate-400 truncate max-w-40">{c.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 font-mono">{c.phone}</td>
                <td className="px-4 py-3"><StageBadge stage={c.stage} /></td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1 max-w-48">
                    {(c.tags || []).slice(0, 2).map(t => <TagPill key={t} tag={t} />)}
                    {(c.tags || []).length > 2 && (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">+{c.tags.length - 2}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 max-w-28 truncate">{c.source}</td>
                <td className="px-4 py-3">
                  {c.opted_out
                    ? <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded-full px-2.5 py-0.5"><UserX size={10} />Opted Out</span>
                    : <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-0.5"><CheckCircle size={10} />Active</span>
                  }
                </td>
                <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={(e) => toggleOptOut(e, c)}
                    className="opacity-0 group-hover:opacity-100 text-xs text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded transition-all"
                    data-testid={`optout-btn-${c.id}`}
                  >
                    {c.opted_out ? "Re-activate" : "Opt Out"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {total > 25 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-500">{((page - 1) * 25) + 1}–{Math.min(page * 25, total)} of {total.toLocaleString()}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary py-1.5 px-3 text-xs" data-testid="prev-page">Prev</button>
              <span className="text-xs text-slate-500 flex items-center px-2">Page {page}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * 25 >= total} className="btn-secondary py-1.5 px-3 text-xs" data-testid="next-page">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Card view — mobile only */}
      <div className="md:hidden space-y-3" data-testid="contacts-cards">
        {loading ? (
          <div className="flex justify-center py-10"><div className="w-5 h-5 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /></div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">No contacts found</div>
        ) : contacts.map(c => (
          <div
            key={c.id}
            onClick={() => setSelectedContactId(c.id)}
            className="card p-4 flex items-start gap-3 active:bg-[#FFF0EB] transition-colors"
            data-testid={`contact-card-mobile-${c.id}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${c.opted_out ? "bg-slate-100 text-slate-400" : "bg-[#FFF0EB] text-[#0F1419]"}`}>
              {(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-slate-900 text-sm">{c.first_name} {c.last_name}</p>
                <StageBadge stage={c.stage} />
              </div>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">{c.phone}</p>
              <p className="text-xs text-slate-400 truncate">{c.source}</p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {(c.tags || []).slice(0, 3).map(t => <TagPill key={t} tag={t} />)}
                {(c.tags || []).length > 3 && <span className="text-xs text-slate-400">+{c.tags.length - 3} more</span>}
              </div>
            </div>
            <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
          </div>
        ))}
        {total > 25 && (
          <div className="flex justify-center gap-3 pt-2 pb-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary py-2 px-4 text-sm" data-testid="prev-page-mobile">Prev</button>
            <span className="text-sm text-slate-500 flex items-center">Page {page} of {Math.ceil(total / 25)}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page * 25 >= total} className="btn-secondary py-2 px-4 text-sm" data-testid="next-page-mobile">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
